/**
 * EVERY FAILURE BECOMES ONE TYPED AppError.
 *
 * THE SERVER WORDS ITS OWN REFUSALS (Rachel, 21-09-2026). The backend team
 * send `message` in the account's language, which they hold, so a refusal the
 * server answered is shown exactly as the server wrote it, and this repo keeps
 * no table of sentences for its codes. `code` stays for the rare screen that
 * must BEHAVE differently on one refusal; it never picks words.
 *
 * Our own table words only what the server could not: no answer at all
 * (network, timeout), an answer with no body (a proxy's 502), or a body this
 * build cannot read. `messageKey` is that fallback, and `serverMessage` wins
 * whenever it exists.
 *
 * Status → behaviour (frontend spec §5.2):
 *   400 bad-request      safe request error
 *   401 unauthenticated  session ended → sign in again
 *   403 forbidden        permission-denied state
 *   404 not-found        not-found state
 *   409 conflict         conflict / reconciliation (the refusals live here)
 *   422 validation       field and business-rule messages
 *   429 rate-limited     retry after a moment
 *   5xx server           the server's words, plus the trace id to quote
 *   no response          network (offline) or timeout
 */
import type { MessageKey } from "@/i18n/resources.gen";

export type AppErrorKind =
  | "bad-request"
  | "unauthenticated"
  | "forbidden"
  | "not-found"
  | "conflict"
  | "validation"
  | "rate-limited"
  | "server"
  | "network"
  | "timeout"
  | "contract"
  | "unknown";

export interface Problem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  /** Stable machine code, e.g. "branch.last_open". */
  code?: string;
  params?: Record<string, string | number>;
  /** Field → message(s), worded by the server. */
  errors?: Record<string, string | string[]>;
  /** The refusal, worded by the server. Shown as sent. */
  message?: string;
}

/**
 * The fallback when the server said nothing we can show — `error.*` only, and
 * only the rows that take no parameters. Every server-answered kind shares one
 * generic row: the server's own sentence is what a reader is meant to see, so
 * a per-status sentence of ours would only ever appear when it had none.
 */
type ErrorMessageKey = Extract<
  MessageKey,
  "error.network" | "error.timeout" | "error.server" | "error.unknown"
>;

const FALLBACK: Partial<Record<AppErrorKind, ErrorMessageKey>> = {
  network: "error.network",
  timeout: "error.timeout",
  server: "error.server",
};

export class AppError extends Error {
  readonly kind: AppErrorKind;
  readonly status: number | undefined;
  readonly code: string | undefined;
  readonly params: Record<string, string | number>;
  readonly fieldErrors: Record<string, string[]>;
  readonly traceId: string | undefined;
  /** The server's sentence, shown as sent; undefined when it sent none. */
  readonly serverMessage: string | undefined;
  readonly retryAfterSeconds: number | undefined;
  readonly problem: Problem | undefined;

  constructor(
    kind: AppErrorKind,
    init: {
      status?: number;
      problem?: Problem;
      retryAfterSeconds?: number;
      cause?: unknown;
      message?: string;
    } = {},
  ) {
    super(init.message ?? init.problem?.message ?? init.problem?.detail ?? init.problem?.title ?? kind, {
      cause: init.cause,
    });
    this.name = "AppError";
    this.kind = kind;
    this.status = init.status;
    this.problem = init.problem;
    this.code = init.problem?.code;
    this.params = init.problem?.params ?? {};
    this.traceId = init.problem?.traceId;
    this.serverMessage = init.problem?.message?.trim() || undefined;
    this.retryAfterSeconds = init.retryAfterSeconds;
    this.fieldErrors = normaliseFieldErrors(init.problem?.errors);
  }

  /** Our wording for when `serverMessage` is absent. Never shown in its place. */
  get messageKey(): ErrorMessageKey {
    return FALLBACK[this.kind] ?? "error.unknown";
  }
}

export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError;
}

export function kindForStatus(status: number): AppErrorKind {
  if (status === 400) return "bad-request";
  if (status === 401) return "unauthenticated";
  if (status === 403) return "forbidden";
  if (status === 404) return "not-found";
  if (status === 409) return "conflict";
  if (status === 422) return "validation";
  if (status === 429) return "rate-limited";
  if (status >= 500) return "server";
  return "unknown";
}

/** Reads a failed response into an AppError. Never throws while doing so. */
export async function errorFromResponse(response: Response): Promise<AppError> {
  let problem: Problem | undefined;
  const type = response.headers.get("content-type") ?? "";
  if (type.includes("json")) {
    try {
      const body: unknown = await response.json();
      if (body && typeof body === "object" && !Array.isArray(body)) problem = body;
    } catch {
      // A body that says JSON and is not: keep the status, lose the detail.
    }
  }
  const retry = Number(response.headers.get("retry-after"));
  return new AppError(kindForStatus(response.status), {
    status: response.status,
    ...(problem ? { problem } : {}),
    ...(Number.isFinite(retry) && retry > 0 ? { retryAfterSeconds: retry } : {}),
  });
}

function normaliseFieldErrors(errors: Problem["errors"]): Record<string, string[]> {
  if (!errors || typeof errors !== "object") return {};
  const out: Record<string, string[]> = {};
  for (const [field, value] of Object.entries(errors)) {
    const list = Array.isArray(value) ? value : [value];
    const messages = list.filter((m): m is string => typeof m === "string" && m !== "");
    if (messages.length) out[field] = messages;
  }
  return out;
}

/** Retrying helps only where the server did not answer, or answered that it is overloaded. */
export function isRetryable(error: unknown): boolean {
  if (!isAppError(error)) return false;
  return (
    error.kind === "network" ||
    error.kind === "timeout" ||
    error.kind === "server" ||
    error.kind === "rate-limited"
  );
}
